/**
 * Types + trust-boundary parsing for teamform-api's getAggregations query
 * (src/graphql/schema/aggregations.graphql on teamform-api). Real-time,
 * DB-aggregated stats — raw counts only, no derived/composed values. See
 * src/util/teamAggregationMetrics.ts for the client-side derivations.
 */

export type ChartPoint = {
  key?: string | null;
  value: number;
};

export type Chart = {
  points: ChartPoint[];
};

export type ExternalManager = {
  personId: string;
  directReportsCount: number;
};

export type TeamAggregations = {
  memberCount?: number | undefined;
  memberFteSum?: number | undefined;
  vacancyCount?: number | undefined;
  openRoleCount?: number | undefined;
  agentCount?: number | undefined;
  /** Funded positions, filled or vacant. Design capacity, not people. */
  positionCount?: number | undefined;
  /** Establishment FTE over those positions. Not comparable to memberFteSum. */
  positionFteSum?: number | undefined;
  /** Seats with at least one allocated occupant. */
  positionFilledCount?: number | undefined;
  /** Empty funded seats. */
  positionVacantCount?: number | undefined;
  /** Establishment FTE of filled seats. */
  positionFilledFteSum?: number | undefined;
  /** Establishment FTE of empty seats. */
  positionVacantFteSum?: number | undefined;
  positionDirectReportsMin?: number | null | undefined;
  positionDirectReportsMax?: number | null | undefined;
  positionDirectReportsAvg?: number | null | undefined;
  positionDirectReportsChart?: Chart | null | undefined;
  positionManagerCount?: number | undefined;
  positionUnassignedCount?: number | undefined;
  positionExternalManagers?: ExternalManager[] | undefined;
  positionManagementLayersMin?: number | null | undefined;
  positionManagementLayersMax?: number | null | undefined;
  positionManagementLayersAvg?: number | null | undefined;
  positionManagementLayersChart?: Chart | null | undefined;
  managerCount?: number | undefined;
  unassignedCount?: number | undefined;
  directReportsMin?: number | null | undefined;
  directReportsMax?: number | null | undefined;
  directReportsAvg?: number | null | undefined;
  directReportsChart?: Chart | null | undefined;
  managementLayersMin?: number | null | undefined;
  managementLayersMax?: number | null | undefined;
  managementLayersAvg?: number | null | undefined;
  managementLayersChart?: Chart | null | undefined;
  externalManagers?: ExternalManager[] | undefined;
  multiTeamOwnerCount?: number | undefined;
  noDirectOwnerCount?: number | undefined;
  reportingLineManagerCount?: number | undefined;
  reportingLineUnassignedCount?: number | undefined;
  reportingLineDirectReportsMin?: number | null | undefined;
  reportingLineDirectReportsMax?: number | null | undefined;
  reportingLineDirectReportsAvg?: number | null | undefined;
  reportingLineDirectReportsChart?: Chart | null | undefined;
  reportingLineManagementLayersMin?: number | null | undefined;
  reportingLineManagementLayersMax?: number | null | undefined;
  reportingLineManagementLayersAvg?: number | null | undefined;
  reportingLineManagementLayersChart?: Chart | null | undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const optionalFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const toChart = (value: unknown): Chart | undefined => {
  if (!isRecord(value) || !Array.isArray(value["points"])) return undefined;
  const points: ChartPoint[] = [];
  for (const point of value["points"]) {
    if (!isRecord(point)) continue;
    const pointValue = optionalFiniteNumber(point["value"]);
    if (pointValue === undefined) continue;
    const key = typeof point["key"] === "string" ? point["key"] : null;
    points.push({ key, value: pointValue });
  }
  return { points };
};

const toExternalManagers = (value: unknown): ExternalManager[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const parsed: ExternalManager[] = [];
  for (const em of value) {
    if (!isRecord(em) || typeof em["personId"] !== "string") continue;
    const directReportsCount = optionalFiniteNumber(em["directReportsCount"]);
    if (directReportsCount === undefined) continue;
    parsed.push({ personId: em["personId"], directReportsCount });
  }
  return parsed;
};

/** Validate GraphQL Aggregations.team at the trust boundary; calculators trust TeamAggregations. */
export const toTeamAggregations = (value: unknown): TeamAggregations => {
  if (!isRecord(value)) return {};
  const aggregations: TeamAggregations = {};

  const memberCount = optionalFiniteNumber(value["memberCount"]);
  if (memberCount !== undefined) aggregations.memberCount = memberCount;
  const memberFteSum = optionalFiniteNumber(value["memberFteSum"]);
  if (memberFteSum !== undefined) aggregations.memberFteSum = memberFteSum;
  const vacancyCount = optionalFiniteNumber(value["vacancyCount"]);
  if (vacancyCount !== undefined) aggregations.vacancyCount = vacancyCount;
  const openRoleCount = optionalFiniteNumber(value["openRoleCount"]);
  if (openRoleCount !== undefined) aggregations.openRoleCount = openRoleCount;
  const agentCount = optionalFiniteNumber(value["agentCount"]);
  if (agentCount !== undefined) aggregations.agentCount = agentCount;
  const positionCount = optionalFiniteNumber(value["positionCount"]);
  if (positionCount !== undefined) aggregations.positionCount = positionCount;
  const positionFteSum = optionalFiniteNumber(value["positionFteSum"]);
  if (positionFteSum !== undefined) {
    aggregations.positionFteSum = positionFteSum;
  }
  const positionFilledCount = optionalFiniteNumber(
    value["positionFilledCount"]
  );
  if (positionFilledCount !== undefined) {
    aggregations.positionFilledCount = positionFilledCount;
  }
  const positionVacantCount = optionalFiniteNumber(
    value["positionVacantCount"]
  );
  if (positionVacantCount !== undefined) {
    aggregations.positionVacantCount = positionVacantCount;
  }
  const positionFilledFteSum = optionalFiniteNumber(
    value["positionFilledFteSum"]
  );
  if (positionFilledFteSum !== undefined) {
    aggregations.positionFilledFteSum = positionFilledFteSum;
  }
  const positionVacantFteSum = optionalFiniteNumber(
    value["positionVacantFteSum"]
  );
  if (positionVacantFteSum !== undefined) {
    aggregations.positionVacantFteSum = positionVacantFteSum;
  }
  const positionDirectReportsMin = optionalFiniteNumber(
    value["positionDirectReportsMin"]
  );
  if (positionDirectReportsMin !== undefined) {
    aggregations.positionDirectReportsMin = positionDirectReportsMin;
  }
  const positionDirectReportsMax = optionalFiniteNumber(
    value["positionDirectReportsMax"]
  );
  if (positionDirectReportsMax !== undefined) {
    aggregations.positionDirectReportsMax = positionDirectReportsMax;
  }
  const positionDirectReportsAvg = optionalFiniteNumber(
    value["positionDirectReportsAvg"]
  );
  if (positionDirectReportsAvg !== undefined) {
    aggregations.positionDirectReportsAvg = positionDirectReportsAvg;
  }
  const positionManagementLayersMin = optionalFiniteNumber(
    value["positionManagementLayersMin"]
  );
  if (positionManagementLayersMin !== undefined) {
    aggregations.positionManagementLayersMin = positionManagementLayersMin;
  }
  const positionManagementLayersMax = optionalFiniteNumber(
    value["positionManagementLayersMax"]
  );
  if (positionManagementLayersMax !== undefined) {
    aggregations.positionManagementLayersMax = positionManagementLayersMax;
  }
  const positionManagementLayersAvg = optionalFiniteNumber(
    value["positionManagementLayersAvg"]
  );
  if (positionManagementLayersAvg !== undefined) {
    aggregations.positionManagementLayersAvg = positionManagementLayersAvg;
  }
  const positionDirectReportsChart = toChart(
    value["positionDirectReportsChart"]
  );
  if (positionDirectReportsChart) {
    aggregations.positionDirectReportsChart = positionDirectReportsChart;
  }
  const positionManagementLayersChart = toChart(
    value["positionManagementLayersChart"]
  );
  if (positionManagementLayersChart) {
    aggregations.positionManagementLayersChart = positionManagementLayersChart;
  }
  const positionManagerCount = optionalFiniteNumber(
    value["positionManagerCount"]
  );
  if (positionManagerCount !== undefined) {
    aggregations.positionManagerCount = positionManagerCount;
  }
  const positionUnassignedCount = optionalFiniteNumber(
    value["positionUnassignedCount"]
  );
  if (positionUnassignedCount !== undefined) {
    aggregations.positionUnassignedCount = positionUnassignedCount;
  }
  const positionExternalManagers = toExternalManagers(
    value["positionExternalManagers"]
  );
  if (positionExternalManagers) {
    aggregations.positionExternalManagers = positionExternalManagers;
  }
  const managerCount = optionalFiniteNumber(value["managerCount"]);
  if (managerCount !== undefined) aggregations.managerCount = managerCount;
  const unassignedCount = optionalFiniteNumber(value["unassignedCount"]);
  if (unassignedCount !== undefined) {
    aggregations.unassignedCount = unassignedCount;
  }

  const directReportsMin = optionalFiniteNumber(value["directReportsMin"]);
  if (directReportsMin !== undefined) {
    aggregations.directReportsMin = directReportsMin;
  }
  const directReportsMax = optionalFiniteNumber(value["directReportsMax"]);
  if (directReportsMax !== undefined) {
    aggregations.directReportsMax = directReportsMax;
  }
  const directReportsAvg = optionalFiniteNumber(value["directReportsAvg"]);
  if (directReportsAvg !== undefined) {
    aggregations.directReportsAvg = directReportsAvg;
  }
  const directReportsChart = toChart(value["directReportsChart"]);
  if (directReportsChart) aggregations.directReportsChart = directReportsChart;

  const managementLayersMin = optionalFiniteNumber(
    value["managementLayersMin"]
  );
  if (managementLayersMin !== undefined) {
    aggregations.managementLayersMin = managementLayersMin;
  }
  const managementLayersMax = optionalFiniteNumber(
    value["managementLayersMax"]
  );
  if (managementLayersMax !== undefined) {
    aggregations.managementLayersMax = managementLayersMax;
  }
  const managementLayersAvg = optionalFiniteNumber(
    value["managementLayersAvg"]
  );
  if (managementLayersAvg !== undefined) {
    aggregations.managementLayersAvg = managementLayersAvg;
  }
  const managementLayersChart = toChart(value["managementLayersChart"]);
  if (managementLayersChart) {
    aggregations.managementLayersChart = managementLayersChart;
  }

  const externalManagers = toExternalManagers(value["externalManagers"]);
  if (externalManagers) aggregations.externalManagers = externalManagers;
  const multiTeamOwnerCount = optionalFiniteNumber(
    value["multiTeamOwnerCount"]
  );
  if (multiTeamOwnerCount !== undefined) {
    aggregations.multiTeamOwnerCount = multiTeamOwnerCount;
  }
  const noDirectOwnerCount = optionalFiniteNumber(value["noDirectOwnerCount"]);
  if (noDirectOwnerCount !== undefined) {
    aggregations.noDirectOwnerCount = noDirectOwnerCount;
  }

  const reportingLineManagerCount = optionalFiniteNumber(
    value["reportingLineManagerCount"]
  );
  if (reportingLineManagerCount !== undefined) {
    aggregations.reportingLineManagerCount = reportingLineManagerCount;
  }
  const reportingLineUnassignedCount = optionalFiniteNumber(
    value["reportingLineUnassignedCount"]
  );
  if (reportingLineUnassignedCount !== undefined) {
    aggregations.reportingLineUnassignedCount = reportingLineUnassignedCount;
  }
  const reportingLineDirectReportsMin = optionalFiniteNumber(
    value["reportingLineDirectReportsMin"]
  );
  if (reportingLineDirectReportsMin !== undefined) {
    aggregations.reportingLineDirectReportsMin = reportingLineDirectReportsMin;
  }
  const reportingLineDirectReportsMax = optionalFiniteNumber(
    value["reportingLineDirectReportsMax"]
  );
  if (reportingLineDirectReportsMax !== undefined) {
    aggregations.reportingLineDirectReportsMax = reportingLineDirectReportsMax;
  }
  const reportingLineDirectReportsAvg = optionalFiniteNumber(
    value["reportingLineDirectReportsAvg"]
  );
  if (reportingLineDirectReportsAvg !== undefined) {
    aggregations.reportingLineDirectReportsAvg = reportingLineDirectReportsAvg;
  }
  const reportingLineDirectReportsChart = toChart(
    value["reportingLineDirectReportsChart"]
  );
  if (reportingLineDirectReportsChart) {
    aggregations.reportingLineDirectReportsChart =
      reportingLineDirectReportsChart;
  }
  const reportingLineManagementLayersMin = optionalFiniteNumber(
    value["reportingLineManagementLayersMin"]
  );
  if (reportingLineManagementLayersMin !== undefined) {
    aggregations.reportingLineManagementLayersMin =
      reportingLineManagementLayersMin;
  }
  const reportingLineManagementLayersMax = optionalFiniteNumber(
    value["reportingLineManagementLayersMax"]
  );
  if (reportingLineManagementLayersMax !== undefined) {
    aggregations.reportingLineManagementLayersMax =
      reportingLineManagementLayersMax;
  }
  const reportingLineManagementLayersAvg = optionalFiniteNumber(
    value["reportingLineManagementLayersAvg"]
  );
  if (reportingLineManagementLayersAvg !== undefined) {
    aggregations.reportingLineManagementLayersAvg =
      reportingLineManagementLayersAvg;
  }
  const reportingLineManagementLayersChart = toChart(
    value["reportingLineManagementLayersChart"]
  );
  if (reportingLineManagementLayersChart) {
    aggregations.reportingLineManagementLayersChart =
      reportingLineManagementLayersChart;
  }

  return aggregations;
};
